import {
  useGetAnalyticsCouponsRoi,
  useGetAnalyticsDayHourRevenue,
  useGetAnalyticsMargins,
  useGetAnalyticsPizzaIngredientsMargin,
  useGetAnalyticsRevenue,
} from '@agos/api-client/customers'
import { useGetSalesChannels } from '@agos/api-client/orders'
import {
  channelLabel,
  filterChannelsOf,
  T, formatCents, formatCount, formatPercent, formatDecimal, tt } from '@agos/shared'
import { ButtonGroup, Spinner, StatCard, StatusBadge, cn } from '@agos/ui'
import { AlertTriangle, Calendar, ChefHat, Repeat, Tag, TrendingUp, Wallet } from 'lucide-react'
import { Fragment, useMemo, useState } from 'react'

import { WebTrackingCard } from './WebTrackingCard'
import {
  cents,
  channelSlices,
  checkRevenue,
  compareDiscounts,
  couponRoi,
  couponTotals,
  dayCountParams,
  dayWindowNote,
  fromToParams,
  heatGrid,
  initialPeriod,
  isoDay,
  marginCoverage,
  margins,
  periodLabel,
  pizzaTotals,
  repeatShare,
  sizeMargins,
  sortMargins,
  type CouponRow,
  type HeatCell,
  type MarginRow,
  type MarginSort,
  type Period,
  type PizzaMarginResponse,
  type PresetDays,
  type RevenueResponse,
} from './analytics-report'

/**
 * The *Tržby* tab — the money, and four cards that cannot follow it.
 *
 * **FB-115.** The period selector offers day presets and a custom range. `/analytics/revenue` and
 * `/analytics/day-hour-revenue` take `from`/`to` and honour it. The coupon, margin and pizza cards
 * take **`days` only**, counted back from `NOW()` — so on a custom range they report a different
 * window from the one on screen. Legacy computes an `effectiveDays` and sends it, silently. Here the
 * day count is still sent, because it is the closest these endpoints accept, and every affected card
 * carries a line saying what window the server actually used.
 *
 * **FB-112.** The margin report's cost is missing for twenty-five of thirty products and the server
 * calls that a 100 % margin. Ten of the twenty-five have a recipe — the missing input is the stock
 * price, not the recipe — and legacy's footnote blames the recipes.
 *
 * **FB-117, FB-118.** The custom-pizza card's topping count is the constant 2, and its revenue is
 * re-derived from an average when the exact figure is in the payload.
 */

const PRESETS: readonly PresetDays[] = [1, 7, 30, 90, 365]

/**
 * Read only when `/sales-channels` has not answered yet — §9.121. The franchise's own names win, and
 * they must: the owner had already renamed `bar` to *Osobný odber* and this map went on saying *Bar*.
 */
const CHANNEL_FALLBACKS: Record<string, string> = {
  delivery: tt('Rozvoz'),
  restaurant: tt('Reštaurácia'),
  bar: tt('Osobný odber'),
  na_sekeru: tt('Na sekeru'),
}

/** Weekday initials for the grid's row labels — two letters, the same in every Slovak calendar. */
// eslint-disable-next-line no-restricted-syntax -- abbreviations, not prose; identical in every locale we ship
const WEEKDAYS: readonly string[] = ['Po', 'Ut', 'St', 'Št', 'Pi', 'So', 'Ne']

export function RevenueTab() {
  const { data: channelData } = useGetSalesChannels()
  const channels = filterChannelsOf(channelData)

  const [today] = useState(() => new Date())
  const [period, setPeriod] = useState<Period>(() => initialPeriod(today))

  const { data, isLoading } = useGetAnalyticsRevenue(fromToParams(period))
  const response = (data ?? {}) as RevenueResponse
  const check = checkRevenue(response)
  const slices = useMemo(() => channelSlices(response.channelSplit ?? []), [response.channelSplit])
  const discounts = compareDiscounts(response.discountImpact ?? [])
  const repeat = repeatShare(response.repeatRate)

  const setPreset = (days: PresetDays) =>
    setPeriod((current) => ({ ...current, kind: 'preset', days }))

  return (
    <div className="space-y-4">
      {/* ─── the period ─── */}
      <div className="flex items-center gap-2 flex-wrap" data-period>
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          <T>Obdobie</T>
        </span>
        <ButtonGroup
          options={PRESETS.map((days) => ({
            value: String(days),
            label: days === 1 ? tt('Dnes') : days === 365 ? tt('1 rok') : `${days} ${tt('dní')}`,
          }))}
          value={period.kind === 'preset' ? String(period.days) : ''}
          onChange={(value) => setPreset(Number(value) as PresetDays)}
        />
        <button
          type="button"
          onClick={() =>
            setPeriod((current) => ({
              ...current,
              kind: current.kind === 'custom' ? 'preset' : 'custom',
            }))
          }
          aria-pressed={period.kind === 'custom'}
          className={cn(
            'px-3 py-1.5 rounded-lg text-xs font-bold',
            period.kind === 'custom'
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted/50 text-muted-foreground hover:bg-muted',
          )}
        >
          <T>Vlastný rozsah</T>
        </button>
        {period.kind === 'custom' && (
          <div className="flex items-center gap-2 pl-2 border-l border-border">
            <input
              type="date"
              value={period.from}
              max={period.to}
              aria-label={tt('Od dátumu')}
              onChange={(event) => setPeriod((current) => ({ ...current, from: event.target.value }))}
              className="px-2 py-1 rounded-md border border-border bg-background text-xs"
            />
            <span className="text-xs text-muted-foreground">→</span>
            <input
              type="date"
              value={period.to}
              min={period.from}
              max={isoDay(today)}
              aria-label={tt('Do dátumu')}
              onChange={(event) => setPeriod((current) => ({ ...current, to: event.target.value }))}
              className="px-2 py-1 rounded-md border border-border bg-background text-xs"
            />
          </div>
        )}
        <span className="ml-auto text-[11px] text-muted-foreground italic" data-period-label>
          {periodLabel(period)}
        </span>
      </div>

      {/*
        FB-643. Legacy renders its `WebTrackingSettingsCard` at exactly this point - after the period
        row, before the loading branch (`analytics.tsx:521`) - and the card gates itself on
        `main_admin`, which is who may store the section.
      */}
      <WebTrackingCard />

      {isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-revenue-headline>
            <StatCard
              label={tt('Tržby')}
              value={formatCents(check.headlineCents)}
              icon={Wallet}
              color="emerald"
            />
            <StatCard
              label={tt('Objednávky')}
              value={formatCount(check.headlineOrders)}
              icon={TrendingUp}
            />
            <StatCard
              label={tt('Priemerná objednávka')}
              value={formatCents(cents(response.headline?.avg_order_value))}
              icon={Wallet}
            />
            <StatCard
              label={tt('Zľavy spolu')}
              value={`−${formatCents(cents(response.headline?.total_discount))}`}
              icon={Tag}
              color="amber"
            />
          </div>

          {/* Four views of one window. They agree to the cent on this data; the day they stop is the
              day somebody spends an afternoon on it, so the check is on the page. */}
          <p
            className="text-[11px] text-muted-foreground"
            data-revenue-agreement={check.agrees && check.ordersAgree ? 'ok' : 'broken'}
          >
            {check.agrees && check.ordersAgree ? (
              <>
                <T>Kanály, dni aj rozdelenie podľa zliav dávajú rovnaký súčet</T>:{' '}
                <strong>{formatCents(check.headlineCents)}</strong> <T>a</T>{' '}
                <strong>{formatCount(check.headlineOrders)}</strong> <T>objednávok</T>.
              </>
            ) : (
              <span className="text-red-500 font-semibold">
                <T>Súčty si neodpovedajú - najväčší rozdiel</T>{' '}
                {formatCents(check.worstGapCents)}. <T>Kanály</T>{' '}
                {formatCents(check.channelCents)}, <T>dni</T> {formatCents(check.dayCents)},{' '}
                <T>zľavy</T> {formatCents(check.bucketCents)}.
              </span>
            )}
          </p>

          <AverageOrderTrend response={response} />

          {/* ─── channels ─── */}
          <div className="bg-card rounded-2xl border border-border p-5" data-channels>
            <h3 className="font-bold mb-3 flex items-center gap-2">
              <Wallet className="w-4 h-4 text-primary" /> <T>Tržby podľa kanála</T>
            </h3>
            {slices.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                <T>Žiadne dáta v tomto období.</T>
              </p>
            ) : (
              <div className="space-y-3">
                {slices.map((slice) => (
                  <div key={slice.type} className="flex items-center gap-3" data-channel={slice.type}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-sm">
                          {channelLabel(slice.type, channels, CHANNEL_FALLBACKS)}
                        </p>
                        <p className="text-sm tabular-nums">
                          <span className="font-bold">{formatCents(slice.revenueCents)}</span>
                          <span className="text-muted-foreground ml-2 text-xs">
                            {formatPercent(slice.sharePercent, 1)}
                          </span>
                        </p>
                      </div>
                      <div className="mt-1 h-2 rounded-full bg-muted overflow-hidden">
                        <div
                          className="h-full bg-primary"
                          style={{ width: `${slice.sharePercent}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">
                        {formatCount(slice.orders)} <T>objednávok · priemer</T>{' '}
                        {formatCents(slice.avgCents)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ─── discounts ─── */}
          <div className="bg-card rounded-2xl border border-border p-5" data-discounts>
            <h3 className="font-bold mb-1 flex items-center gap-2">
              <Tag className="w-4 h-4 text-primary" /> <T>Vplyv zliav</T>
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              <T>Objednávky s kupónom alebo zľavou proti tým bez</T>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(
                [
                  { key: 'with', label: tt('So zľavou'), row: discounts.withDiscount, tone: 'border-l-emerald-500' },
                  { key: 'without', label: tt('Bez zľavy'), row: discounts.without, tone: 'border-l-zinc-400' },
                ] as const
              ).map((side) => (
                <div
                  key={side.key}
                  className={cn('rounded-xl border border-border border-l-4 p-3', side.tone)}
                  data-discount-bucket={side.key}
                >
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    {side.label}
                  </p>
                  <p className="text-2xl font-bold mt-1 tabular-nums">
                    {formatCount(side.row?.orders ?? 0)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <T>objednávok</T>
                  </p>
                  <dl className="mt-2 pt-2 border-t border-border space-y-1 text-xs">
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">
                        <T>Tržby</T>
                      </dt>
                      <dd className="font-bold tabular-nums">{formatCents(cents(side.row?.revenue))}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">
                        <T>Priemerná objednávka</T>
                      </dt>
                      <dd className="font-bold tabular-nums">
                        {formatCents(cents(side.row?.avg_order_value))}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground">
                        <T>Daná zľava</T>
                      </dt>
                      <dd className="font-bold tabular-nums text-amber-500">
                        −{formatCents(cents(side.row?.total_discount_given))}
                      </dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>
            {discounts.liftPercent !== null && (
              <p className="text-xs text-muted-foreground mt-3 italic" data-discount-lift>
                <T>Priemerná objednávka so zľavou je o</T>{' '}
                <strong
                  className={discounts.liftPercent > 0 ? 'text-emerald-500' : 'text-amber-500'}
                >
                  {formatPercent(Math.abs(discounts.liftPercent), 1)}
                </strong>{' '}
                {discounts.liftPercent > 0 ? <T>vyššia</T> : <T>nižšia</T>}.
              </p>
            )}
          </div>

          {/* ─── repeat rate ─── */}
          <div className="bg-card rounded-2xl border border-border p-5" data-repeat>
            <h3 className="font-bold mb-1 flex items-center gap-2">
              <Repeat className="w-4 h-4 text-primary" /> <T>Vrátenie zákazníkov</T>
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              <T>Koľko zákazníkov objednalo v tomto období viac ako raz</T>
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard label={tt('Unikátnych')} value={formatCount(repeat.total)} />
              <StatCard
                label={tt('Vrátili sa')}
                value={formatCount(repeat.repeat)}
                sub={repeat.repeatPercent === null ? tt('nemerané') : formatPercent(repeat.repeatPercent, 1)}
                color="emerald"
              />
              <StatCard
                label={tt('Trikrát a viac')}
                value={formatCount(repeat.frequent)}
                sub={
                  repeat.frequentPercent === null ? tt('nemerané') : formatPercent(repeat.frequentPercent, 1)
                }
                color="amber"
              />
              <StatCard
                label={tt('Objednávok na zákazníka')}
                value={formatDecimal(repeat.avgOrders, 1)}
              />
            </div>
          </div>

          <DayHourCard period={period} />
          <CouponRoiCard period={period} />
          <MarginCard period={period} />
          <PizzaMarginCard period={period} />
        </>
      )}
    </div>
  )
}

/** The average order over time, drawn in CSS so the figures stay assertable text. */
function AverageOrderTrend({ response }: { response: RevenueResponse }) {
  const days = response.dailyTrend ?? []
  if (days.length === 0) return null
  const peak = days.reduce((best, day) => (cents(day.avg_order_value) > cents(best.avg_order_value) ? day : best), days[0]!)
  const peakCents = cents(peak.avg_order_value)

  return (
    <div className="bg-card rounded-2xl border border-border p-5" data-aov-trend>
      <h3 className="font-bold mb-1 flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-primary" /> <T>Priemerná objednávka v čase</T>
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        <T>Najvyššia</T> {formatCents(peakCents)} <T>dňa</T> {peak.day}
      </p>
      <div className="flex items-end gap-px h-40">
        {days.map((day) => {
          const value = cents(day.avg_order_value)
          return (
            <div
              key={day.day}
              data-aov-bar={day.day}
              title={`${day.day ?? ''} - ${formatCents(value)} · ${formatCount(day.orders ?? 0)}`}
              className="flex-1 min-w-[2px] bg-primary/70 hover:bg-primary rounded-t"
              style={{ height: `${peakCents === 0 ? 0 : (value / peakCents) * 100}%` }}
            />
          )
        })}
      </div>
    </div>
  )
}

function DayHourCard({ period }: { period: Period }) {
  // This endpoint takes `from`/`to`, so it follows a custom range exactly.
  const { data, isLoading } = useGetAnalyticsDayHourRevenue(fromToParams(period))
  const grid = useMemo(() => heatGrid(((data ?? {}) as { cells?: HeatCell[] }).cells ?? []), [data])

  return (
    <div className="bg-card rounded-2xl border border-border p-5" data-day-hour>
      <h3 className="font-bold mb-1 flex items-center gap-2">
        <Calendar className="w-4 h-4 text-primary" /> <T>Tržby podľa dňa a hodiny</T>
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        {periodLabel(period)}
        {grid.busiest !== null && (
          <>
            {' · '}
            <T>najsilnejšia hodina</T>{' '}
            <strong>
              {WEEKDAYS[(grid.busiest.dow + 6) % 7]} {grid.busiest.hour}:00
            </strong>{' '}
            ({formatCents(grid.busiest.cents)})
          </>
        )}
      </p>
      {isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div
            className="inline-grid gap-[3px] text-[10px]"
            style={{ gridTemplateColumns: 'auto repeat(24, minmax(28px, 1fr))' }}
          >
            <div />
            {Array.from({ length: 24 }, (_, hour) => (
              <div key={hour} className="font-bold text-muted-foreground text-center pb-1.5">
                {hour}
              </div>
            ))}
            {grid.rows.map((row, index) => (
              <Fragment key={WEEKDAYS[index]}>
                <div className="font-bold text-muted-foreground pr-2 flex items-center justify-end">
                  {WEEKDAYS[index]}
                </div>
                {row.map((value, hour) => {
                  const ratio = grid.maxCents === 0 ? 0 : value / grid.maxCents
                  return (
                    <div
                      key={hour}
                      data-heat-cell
                      title={`${WEEKDAYS[index] ?? ''} ${hour}:00 - ${formatCents(value)}`}
                      className="rounded flex items-center justify-center h-[26px] text-[9px] font-bold tabular-nums"
                      style={{
                        background:
                          value > 0
                            ? `rgba(249, 115, 22, ${0.1 + ratio * 0.9})`
                            : 'rgba(120, 120, 120, 0.06)',
                        color: ratio > 0.55 ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.55)',
                      }}
                    >
                      {value > 0 ? Math.round(value / 100) : ''}
                    </div>
                  )
                })}
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

/** A line stating what window a `days`-only card really covers. FB-115. */
function WindowNote({
  period,
  serverWindowDays,
}: {
  period: Period
  serverWindowDays: number | undefined
}) {
  const note = dayWindowNote(period, serverWindowDays)
  if (note === null) return null
  return (
    <p className="text-[11px] text-amber-500 mt-1" data-window-note>
      {note}
    </p>
  )
}

function CouponRoiCard({ period }: { period: Period }) {
  const { data, isLoading } = useGetAnalyticsCouponsRoi(dayCountParams(period))
  const response = (data ?? {}) as { coupons?: CouponRow[]; windowDays?: number }
  const rows = useMemo(() => couponRoi(response.coupons ?? []), [response.coupons])
  const totals = couponTotals(rows)

  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden" data-coupon-roi>
      <div className="px-5 py-4 border-b border-border">
        <h3 className="font-bold flex items-center gap-2">
          <Tag className="w-4 h-4 text-primary" /> <T>Návratnosť kupónov</T>
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          <T>Tržby z objednávok s kupónom mínus daná zľava</T>
        </p>
        <WindowNote period={period} serverWindowDays={response.windowDays} />
      </div>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <p className="p-6 text-center text-sm text-muted-foreground">
          <T>Žiadne použité kupóny v tomto období.</T>
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-muted/50 text-xs">
              <tr>
                <th className="px-4 py-2 text-left font-bold text-muted-foreground uppercase">
                  <T>Kód</T>
                </th>
                <th className="px-4 py-2 text-right font-bold text-muted-foreground uppercase">
                  <T>Použitý</T>
                </th>
                <th className="px-4 py-2 text-right font-bold text-muted-foreground uppercase">
                  <T>Tržba</T>
                </th>
                <th className="px-4 py-2 text-right font-bold text-muted-foreground uppercase">
                  <T>Zľava</T>
                </th>
                <th className="px-4 py-2 text-right font-bold text-muted-foreground uppercase">
                  <T>Netto</T>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.code} className="border-t border-border" data-coupon={row.code}>
                  <td className="px-4 py-2">
                    <p className="font-mono font-bold">{row.code}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {row.type === 'percent'
                        ? formatPercent(row.value, 0)
                        : formatCents(Math.round(row.value * 100))}
                    </p>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatCount(row.uses)}×</td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold">
                    {formatCents(row.revenueCents)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-amber-500">
                    −{formatCents(row.discountCents)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    <span className={cn('font-bold', row.netCents > 0 ? 'text-emerald-500' : 'text-red-500')}>
                      {formatCents(row.netCents)}
                    </span>
                    {/* Legacy prints `(0%)` when nothing was given away, which reads as a coupon that
                        returned nothing. */}
                    <span className="text-[10px] text-muted-foreground ml-1" data-coupon-roi-percent>
                      {row.roiPercent === null ? tt('(bez zľavy)') : `(${formatPercent(row.roiPercent, 0)})`}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-muted/30 font-bold">
              {/* Legacy has no total row, so nobody sees what the coupons cost in aggregate. */}
              <tr data-coupon-totals>
                <td className="px-4 py-2">
                  <T>Spolu</T>
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatCount(rows.reduce((sum, row) => sum + row.uses, 0))}×
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {formatCents(totals.revenueCents)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-amber-500">
                  −{formatCents(totals.discountCents)}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{formatCents(totals.netCents)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}

const MARGIN_SORTS: readonly { value: MarginSort; label: string }[] = [
  { value: 'profit', label: tt('Zisk') },
  { value: 'margin', label: tt('Marža') },
  { value: 'units', label: tt('Predaj') },
]

/**
 * **§9.314, FB-492.** The server's `LIMIT` for this list, not a tighter bound of our own.
 *
 * It was 25 - legacy's `sorted.slice(0, 25)` at `analytics.tsx:1115`, faithfully ported over the same
 * `LIMIT 30`. Two bounds for one list, and the tighter one applied *after* a sort that sends every
 * unmeasured row to the bottom, so the coverage panel above could say *"3 have a recipe whose
 * ingredients are unpriced"* while all three sat at 26, 28 and 30 of 30 and the table drew 25.
 * Legacy hid five rows nobody was looking for; AGOS hid the five it had just named.
 */
const MARGIN_ROW_CAP = 30

function MarginCard({ period }: { period: Period }) {
  const [sort, setSort] = useState<MarginSort>('profit')
  const { data, isLoading } = useGetAnalyticsMargins(dayCountParams(period))
  const response = (data ?? {}) as { products?: MarginRow[]; windowDays?: number }
  const rows = useMemo(() => margins(response.products ?? []), [response.products])
  const coverage = marginCoverage(rows)
  const sorted = useMemo(() => sortMargins(rows, sort), [rows, sort])

  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden" data-margins>
      <div className="px-5 py-4 border-b border-border flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div>
          <h3 className="font-bold flex items-center gap-2">
            <ChefHat className="w-4 h-4 text-primary" /> <T>Marža na produkt</T>
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            <T>Cena mínus surovinová cena z receptu a nákladovej ceny skladu</T>
          </p>
          <WindowNote period={period} serverWindowDays={response.windowDays} />
        </div>
        <ButtonGroup
          options={MARGIN_SORTS.map((entry) => ({ value: entry.value, label: entry.label }))}
          value={sort}
          onChange={(value) => setSort(value as MarginSort)}
        />
      </div>

      {/* FB-112. Two different reasons a cost is missing, and only one of them is a missing recipe. */}
      {(coverage.noRecipe > 0 || coverage.recipeUnpriced > 0) && (
        <div
          className="px-5 py-3 border-b border-border bg-amber-500/5 text-xs space-y-1"
          data-margin-coverage
        >
          <p className="font-bold text-amber-500 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            <T>Maržu nemožno spočítať pre</T> {formatCount(coverage.noRecipe + coverage.recipeUnpriced)}{' '}
            <T>z</T> {formatCount(coverage.total)} <T>produktov</T>
          </p>
          <p className="text-muted-foreground">
            <strong>{formatCount(coverage.noRecipe)}</strong> <T>bez receptu</T> ·{' '}
            <strong>{formatCount(coverage.recipeUnpriced)}</strong>{' '}
            <T>
              má recept, ale suroviny v ňom nemajú nákladovú cenu - tam nechýba recept, ale cena na
              sklade
            </T>
            .
          </p>
          {coverage.overstatedShare !== null && coverage.overstatedShare > 0 && (
            <p className="text-muted-foreground" data-margin-overstated>
              <T>Server pre ne vracia maržu 100 % a zisk rovný tržbe. To je</T>{' '}
              <strong>{formatCents(coverage.unpricedRevenueCents)}</strong>{' '}
              <T>z</T>{' '}
              <strong>
                {formatCents(coverage.unpricedRevenueCents + coverage.knownProfitCents)}
              </strong>{' '}
              (<strong>{formatPercent(coverage.overstatedShare, 1)}</strong>){' '}
              <T>„zisku“ v tomto prehľade. Tu sa taký riadok ukazuje ako nezmeraný.</T>
            </p>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : sorted.length === 0 ? (
        <p className="p-6 text-center text-sm text-muted-foreground">
          <T>Žiadne dáta o produktoch.</T>
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead className="bg-muted/50 text-xs">
              <tr>
                <th className="px-4 py-2 text-left font-bold text-muted-foreground uppercase">
                  <T>Produkt</T>
                </th>
                <th className="px-4 py-2 text-right font-bold text-muted-foreground uppercase">
                  <T>Cena</T>
                </th>
                <th className="px-4 py-2 text-right font-bold text-muted-foreground uppercase">
                  <T>Náklady</T>
                </th>
                <th className="px-4 py-2 text-center font-bold text-muted-foreground uppercase">
                  <T>Zdroj</T>
                </th>
                <th className="px-4 py-2 text-right font-bold text-muted-foreground uppercase">
                  <T>Marža</T>
                </th>
                <th className="px-4 py-2 text-right font-bold text-muted-foreground uppercase">
                  <T>Predané</T>
                </th>
                <th className="px-4 py-2 text-right font-bold text-muted-foreground uppercase">
                  <T>Zisk</T>
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.slice(0, MARGIN_ROW_CAP).map((row) => (
                <tr
                  key={row.id}
                  className={cn(
                    'border-t border-border',
                    row.marginPercent !== null && row.marginPercent < 20 && 'bg-red-500/5',
                  )}
                  data-margin-row
                  data-cost-state={row.state}
                >
                  <td className="px-4 py-2 font-medium truncate max-w-[260px]">{row.name}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatCents(row.priceCents)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-muted-foreground">
                    {row.state === 'priced' ? formatCents(row.costCents) : '-'}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <StatusBadge
                      label={
                        row.state === 'priced'
                          ? tt('recept')
                          : row.state === 'recipe-unpriced'
                            ? tt('recept bez cien')
                            : tt('bez receptu')
                      }
                      color={row.state === 'priced' ? 'violet' : 'amber'}
                    />
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums" data-margin-percent>
                    {row.marginPercent === null ? (
                      <span className="text-amber-500 font-semibold">
                        <T>nemerané</T>
                      </span>
                    ) : (
                      <span
                        className={cn(
                          'font-bold',
                          row.marginPercent >= 50
                            ? 'text-emerald-500'
                            : row.marginPercent >= 20
                              ? 'text-amber-500'
                              : 'text-red-500',
                        )}
                      >
                        {formatPercent(row.marginPercent, 1)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{formatCount(row.units)}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold" data-margin-profit>
                    {/* Legacy shows the revenue here when there is no cost, and sorts by it. */}
                    {row.profitCents === null ? (
                      <span className="text-amber-500 font-semibold">
                        <T>nemerané</T>
                      </span>
                    ) : (
                      formatCents(row.profitCents)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sorted.length > MARGIN_ROW_CAP && (
            <p className="text-[11px] text-muted-foreground px-4 py-2 border-t border-border">
              <T>Zobrazených</T> {formatCount(MARGIN_ROW_CAP)} <T>z</T>{' '}
              {formatCount(sorted.length)} <T>produktov</T>
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function PizzaMarginCard({ period }: { period: Period }) {
  const { data, isLoading } = useGetAnalyticsPizzaIngredientsMargin(dayCountParams(period))
  const response = useMemo(() => (data ?? {}) as PizzaMarginResponse, [data])
  const totals = pizzaTotals(response)
  const sizes = useMemo(() => sizeMargins(response), [response])
  const constantToppings = sizes.some((size) => size.missing === 'topping-count')

  if (isLoading) {
    return (
      <div className="bg-card rounded-2xl border border-border p-5 flex justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="bg-card rounded-2xl border border-border p-5" data-pizza-margin>
      <h3 className="font-bold mb-1 flex items-center gap-2">
        <ChefHat className="w-4 h-4 text-primary" /> <T>Vlastná pizza</T>
      </h3>
      <p className="text-xs text-muted-foreground mb-1">
        <T>Objednávky, ktoré si zákazník zložil sám - tie nemajú riadok v produktoch.</T>
      </p>
      <WindowNote period={period} serverWindowDays={response.windowDays} />

      {constantToppings && (
        <div
          className="rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 my-3 text-xs space-y-1"
          data-topping-count-constant
        >
          {/* FB-117. Measured: the real average is 4,84 toppings at 32 cm and 5,00 at 50 cm. */}
          <p className="font-bold text-amber-500 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5" />
            <T>Odhad marže sa tu nedá urobiť</T>
          </p>
          <p className="text-muted-foreground">
            <T>
              Server počíta počet ingrediencií z názvu objednávky tak, že spočíta oddeľovače „·“. V
              názve je vždy práve jeden - oddeľuje základ od zoznamu ingrediencií, ktoré sú medzi
              sebou oddelené čiarkami. Preto vždy vyjde presne
            </T>{' '}
            <strong>2</strong>
            <T>
              , nezávisle od skutočnej pizze. Náklad postavený na tejto konštante by maržu nadhodnotil,
              takže sa neuvádza. Všetko ostatné nižšie je skutočné.
            </T>
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 my-3" data-pizza-totals>
        <StatCard label={tt('Objednávok')} value={formatCount(totals.orders)} />
        <StatCard label={tt('Kusov')} value={formatCount(totals.units)} />
        <StatCard
          // FB-118: the server's own figure. Legacy multiplies an average back and lands 10 cents out.
          label={tt('Tržby')}
          value={formatCents(totals.revenueCents)}
          color="emerald"
        />
        <StatCard
          label={tt('Ingrediencie s cenou')}
          value={`${formatCount(totals.ingredientsPriced)}/${formatCount(totals.ingredientsTotal)}`}
          sub={totals.coveragePercent === null ? undefined : formatPercent(totals.coveragePercent, 0)}
          color={totals.coveragePercent !== null && totals.coveragePercent < 100 ? 'amber' : 'emerald'}
        />
      </div>

      <div className="space-y-3">
        {sizes.map((size) => (
          <div key={size.cm} className="rounded-xl border border-border p-4" data-pizza-size={size.cm}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                  <span className="text-xs font-black text-primary">{size.cm}</span>
                </div>
                <div>
                  <p className="font-bold">{size.cm} cm</p>
                  <p className="text-[10px] text-muted-foreground">
                    {formatCount(size.orders)} <T>objednávok</T>
                  </p>
                </div>
              </div>
              {size.marginPercent === null ? (
                <div className="text-right">
                  <p className="text-sm font-bold text-amber-500" data-pizza-margin-state={size.missing}>
                    <T>nemerané</T>
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {size.missing === 'topping-count' ? (
                      <T>počet ingrediencií je konštanta</T>
                    ) : size.missing === 'base-cost' ? (
                      <T>chýba náklad základu</T>
                    ) : (
                      <T>chýba cena surovín</T>
                    )}
                  </p>
                </div>
              ) : (
                <div className="text-right">
                  {/* Named so a test can tell *a margin was stated* from *a margin was refused* -
                    * the refusal has `data-pizza-margin-state` and this had nothing. */}
                  <p
                    className="text-2xl font-bold tabular-nums text-emerald-500"
                    data-pizza-margin-percent
                  >
                    {formatPercent(size.marginPercent, 0)}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    <T>odhadovaná marža</T>
                  </p>
                </div>
              )}
            </div>
            <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div>
                <dt className="text-muted-foreground uppercase text-[10px]">
                  <T>Priem. cena</T>
                </dt>
                <dd className="font-bold tabular-nums">{formatCents(size.avgPriceCents)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground uppercase text-[10px]">
                  <T>Náklad základu</T>
                </dt>
                <dd className="font-bold tabular-nums">
                  {size.baseCostCents === null || size.baseCostCents === 0 ? (
                    <span className="text-red-500">-</span>
                  ) : (
                    formatCents(size.baseCostCents)
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground uppercase text-[10px]">
                  <T>Priem. ingrediencia</T>
                </dt>
                <dd className="font-bold tabular-nums">{formatCents(size.avgIngredientCents)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground uppercase text-[10px]">
                  <T>Odhad. náklad</T>
                </dt>
                <dd className="font-bold tabular-nums text-amber-500">
                  {size.estimatedCostCents === null ? '-' : formatCents(size.estimatedCostCents)}
                </dd>
              </div>
            </dl>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-muted-foreground italic mt-4 pt-3 border-t border-border">
        <T>
          Tento prehľad je odhad. `order_items` neukladá ingrediencie vlastnej pizze štruktúrovane, iba
          textový názov - presné marže na objednávku by si vyžiadali zmenu v ukladaní objednávok.
        </T>
      </p>
    </div>
  )
}
